import React, { Dispatch, SetStateAction, useContext, useState } from 'react';
import { createSearchParams, useNavigate } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import {
	convertPlatesToArray,
	formatPlates,
	formatDateTimeValues,
	convertVignetteIDateToDates,
	IDates,
	OrderService,
	formatPlatesPayload,
	VignetteContext,
	getErrorTextFromResponse,
	OfferContext,
	getOrderUrlParams,
	getVignetteDates,
	IPlatesValidatedState,
	IProductOrderSummary,
	IDeleteProductProps,
	IUpdateOrder,
	IRemoveOrder,
	setRouteLangParam,
	IProductSummary,
	getLabelValue,
	formatTripsAmountLabel,
	IError,
	ITypeVignettesOrderManagementFields,
	getUrlBasePath,
} from '@adac-mp/shared';
import {
	EnterPlates,
	StartDatePicker,
	ContentLoader,
	Toast,
	Typography,
	CircleCrossIcon,
	ErrorCrossIcon,
	renderCancellationOverlay,
	renderProductSummaryElement,
	DeleteIcon,
} from '../../index';

const orderExceptionHandler = (errors: IError[], labels, setApiError: Dispatch<SetStateAction<string | undefined>>) => {
	const errorText = getErrorTextFromResponse(errors, getLabelValue('mp.generalError', labels));
	setApiError(errorText);
};

const updateOrder = async ({
	orderDetailUuid,
	updatedLicencePlate,
	updatedVignetteDates,
	labels,
	setLoading,
	setApiError,
	updateNewOrderData,
	handleOrderException,
}: IUpdateOrder) => {
	const { value, countryCode: updatedCountryCode, specialPlates } = updatedLicencePlate;
	const updatedLicenseplate = formatPlatesPayload(value);
	const { checkOutDateTime } = getVignetteDates(updatedVignetteDates);
	const body = {
		orderDetailUuid,
		licenseplate: updatedLicenseplate,
		licenseplateType: Number(specialPlates),
		checkOutDateTime,
		remark: '',
		countryCode: updatedCountryCode,
	};

	setLoading(true);
	const token = sessionStorage.getItem('token');
	if (token) {
		await OrderService.updateOrderInfo(body, token).then((res) => {
			if (res.code === 200) {
				updateNewOrderData(res);
			} else {
				handleOrderException(res.errors, labels, setApiError);
			}
			setLoading(false);
		});
	}
};

const removeOrder = async ({
	lang,
	orderDetailUuid,
	navigate,
	labels,
	updateCancelledOrderInfo,
	handleOrderException,
	setLoading,
	setApiError,
}: IRemoveOrder) => {
	setLoading(true);
	const token = sessionStorage.getItem('token');
	if (token) {
		await OrderService.cancelOrder({ orderDetailUuid }, token, lang).then((res) => {
			if (res.code === 200) {
				const { orderInfo } = res;
				const cancelledOrderDetails = orderInfo.orderDetails.filter(
					(orderDetail) => orderDetail.orderDetailUuid === orderDetailUuid,
				);
				const isFlexProduct = cancelledOrderDetails.some((orderDetail) => orderDetail.flexProduct);
				updateCancelledOrderInfo({
					...orderInfo,
					orderDetails: cancelledOrderDetails,
					flexProduct: isFlexProduct,
				});
				const orderHash = getOrderUrlParams(window.location);
				const params = createSearchParams();
				params.set('orderHash', orderHash);
				navigate(getUrlBasePath(`/cancel-order-confirmation${setRouteLangParam(lang)}`, params));
			} else {
				handleOrderException(res.errors, labels, setApiError);
			}
			setLoading(false);
		});
	}
};

const deleteOrderedProduct = ({
	lang,
	currOrderItem,
	navigate,
	cartLabels,
	labels,
	setLoading,
	setApiError,
	handleOrderException,
	updateCancelledOrderInfo,
}: IDeleteProductProps): IProductSummary => {
	const { orderDetailUuid, serviceCatalogName, moduleDetail, refundAmount, price, supplierId } = currOrderItem;

	const hasPartialCancellation =
		refundAmount?.decimalAmount > 0 && refundAmount?.decimalAmount !== price.decimalAmount && supplierId === 3;
	return {
		id: 'delete product',
		type: 'cart',
		label: `${serviceCatalogName} ${cartLabels?.cancel || 'stornieren'}`,
		value: (
			<span className="w-full flex gap-1.5 justify-end">
				<CircleCrossIcon size="20" />
				<Typography variant="title">{cartLabels?.cancelOrderLabel || 'Dieses Produkt stornieren'}</Typography>
			</span>
		),
		component: renderCancellationOverlay(currOrderItem, cartLabels),
		handleChange: () => {
			removeOrder({
				lang,
				navigate,
				orderDetailUuid,
				labels,
				setLoading,
				setApiError,
				handleOrderException,
				updateCancelledOrderInfo,
			});
		},
		submitTracking: {
			button_click: {
				element: 'Produkt stornieren',
				label: 'Stornierung erfolgreich',
				category: 'Account',
				product: serviceCatalogName,
				productVariant: `${moduleDetail} | ${hasPartialCancellation ? 'Teilerstattung' : 'Vollerstattung'}`,
			},
		},
		submitButtonLabel: cartLabels?.confirmCancelation || 'Stornierung bestätigen',
	};
};

export const VignetteOrderSummary = ({ currOrderItem }: IProductOrderSummary) => {
	const navigate = useNavigate();
	const { contentfulLabels, updateNewOrderData } = useContext(VignetteContext);
	const currentLabels = contentfulLabels as ITypeVignettesOrderManagementFields;
	const { lang, labels, updateCancelledOrderInfo } = useContext(OfferContext);
	const [apiError, setApiError] = useState<string | undefined>(undefined);
	const [hideModal, setHideModal] = useState<boolean>(false);
	const [loading, setLoading] = useState(false);
	const {
		checkInDate,
		checkOutDate,
		changelicenseplate,
		changevalidity,
		cancelable,
		countryCode,
		licenseplate,
		licenseplateType,
		customerCountries,
		orderDetailUuid,
	} = currOrderItem;
	const [vignetteDates, setVignetteDates] = useState<IDates | undefined>(
		convertVignetteIDateToDates(checkInDate, checkOutDate),
	);
	const [updatedLicencePlate, setUpdatedLicencePlate] = useState<IPlatesValidatedState>({
		value: convertPlatesToArray(currOrderItem.licenseplate, countryCode, Boolean(licenseplateType)),
		error: false,
		confirmed: false,
		countryCode,
		specialPlates: Boolean(licenseplateType),
	});

	const productSummary: IProductSummary[] = [
		{
			id: 'productId',
			type: undefined,
			label: 'Product-ID',
			value: currOrderItem ? (
				String(currOrderItem.supplierBookingNo)
			) : (
				<Skeleton inline height="100%" width="100%" />
			),
		},
		{
			id: 'product',
			type: undefined,
			label: getLabelValue('mp.article', labels) || 'Artikel',
			value: currOrderItem ? (
				currOrderItem?.serviceCatalogName.trim()
			) : (
				<Skeleton inline height="100%" width="100%" />
			),
		},
		{
			id: 'dates',
			type: changevalidity ? 'edit' : undefined,
			label: getLabelValue('mp.valid', labels) || 'Gültig',
			submitTracking: {
				button_click: {
					element: 'Gültigkeitszeitraum ändern',
					label: 'Änderung erfolgreich',
					category: 'Account',
					trackOnce: true,
				},
			},
			value: currOrderItem ? (
				formatDateTimeValues(checkOutDate, checkInDate, lang, true, labels)
			) : (
				<Skeleton inline height="100%" width="100%" />
			),
			component: (
				<StartDatePicker
					inline
					dates={vignetteDates}
					offer={currOrderItem}
					onStartDateChange={(_, dates) => {
						setVignetteDates(dates);
					}}
				/>
			),
			handleChange: () => {
				if (vignetteDates) {
					updateOrder({
						orderDetailUuid,
						updatedLicencePlate,
						updatedVignetteDates: vignetteDates,
						labels,
						setLoading,
						setApiError,
						handleOrderException: orderExceptionHandler,
						updateNewOrderData,
					});
				}
			},
			handleClose: () => {
				setVignetteDates(convertVignetteIDateToDates(checkInDate, checkOutDate));
			},
		},
		{
			id: 'licenseplate',
			type: changelicenseplate ? 'edit' : undefined,
			label: getLabelValue('mp.licensePlate', labels) || 'Kennzeichen',
			value: currOrderItem ? (
				formatPlates(currOrderItem.licenseplate)
			) : (
				<Skeleton inline height="100%" width="100%" />
			),
			component: (
				<EnterPlates
					withAutoFocus
					border
					hasSpecialPlates
					insideOfModal
					plates={updatedLicencePlate}
					countryCode={countryCode}
					setPlates={(value: IPlatesValidatedState) => {
						setUpdatedLicencePlate(value);
					}}
					customerCountries={customerCountries}
					setHideModal={setHideModal}
				/>
			),
			hasErrors: !updatedLicencePlate.confirmed || updatedLicencePlate.error,
			hidden: hideModal,
			submitTracking: {
				button_click: {
					element: 'Kennzeichen ändern',
					label: 'Änderung erfolgreich',
					category: 'Account',
					trackOnce: true,
				},
			},
			handleChange: () => {
				if (updatedLicencePlate.confirmed && vignetteDates) {
					updateOrder({
						orderDetailUuid,
						updatedLicencePlate,
						updatedVignetteDates: vignetteDates,
						labels,
						setLoading,
						setApiError,
						handleOrderException: orderExceptionHandler,
						updateNewOrderData,
					});
				}
			},
			handleClose: () => {
				if (licenseplate !== updatedLicencePlate.value) {
					setUpdatedLicencePlate({
						...updatedLicencePlate,
						value: convertPlatesToArray(licenseplate, countryCode, Boolean(licenseplateType)),
						specialPlates: Boolean(licenseplateType),
						error: false,
					});
				}
			},
		},
	];

	if (cancelable) {
		productSummary.push(
			deleteOrderedProduct({
				lang,
				currOrderItem,
				cartLabels: currentLabels,
				labels,
				navigate,
				setLoading,
				setApiError,
				handleOrderException: orderExceptionHandler,
				updateCancelledOrderInfo,
			}),
		);
	}

	return (
		<>
			<ContentLoader isLoading={loading} />
			{renderProductSummaryElement(productSummary, currOrderItem)}
			{apiError && (
				<Toast content={apiError} icon={<ErrorCrossIcon />} onToastFinished={() => setApiError(undefined)} />
			)}
		</>
	);
};

export const TollSectionOrderSummary = ({ currOrderItem }: IProductOrderSummary) => {
	const navigate = useNavigate();
	const { contentfulLabels, updateNewOrderData } = useContext(VignetteContext);
	const currentLabels = contentfulLabels as ITypeVignettesOrderManagementFields;
	const { lang, labels, updateCancelledOrderInfo } = useContext(OfferContext);
	const [apiError, setApiError] = useState<string | undefined>(undefined);
	const [hideModal, setHideModal] = useState<boolean>(false);
	const [loading, setLoading] = useState(false);
	const {
		checkInDate,
		checkOutDate,
		changelicenseplate,
		changevalidity,
		cancelable,
		countryCode,
		licenseplate,
		licenseplateType,
		customerCountries,
		orderDetailUuid,
	} = currOrderItem;
	const [updatedLicencePlate, setUpdatedLicencePlate] = useState<IPlatesValidatedState>({
		value: convertPlatesToArray(currOrderItem.licenseplate, countryCode, Boolean(licenseplateType)),
		error: false,
		confirmed: false,
		countryCode,
		specialPlates: Boolean(licenseplateType),
	});
	const vignetteDates = convertVignetteIDateToDates(checkInDate, checkOutDate);

	const productSummary: IProductSummary[] = [
		{
			id: 'productId',
			type: undefined,
			label: 'Product-ID',
			value: currOrderItem ? (
				String(currOrderItem.supplierBookingNo)
			) : (
				<Skeleton inline height="100%" width="100%" />
			),
		},
		{
			id: 'dates',
			label: getLabelValue('mp.valid', labels) || 'Gültig',
			value: currOrderItem ? (
				formatDateTimeValues(checkOutDate, checkInDate, lang, true, labels)
			) : (
				<Skeleton inline height="100%" width="100%" />
			),
		},
		{
			id: 'licenseplate',
			type: changevalidity ? 'edit' : undefined,
			label: getLabelValue('mp.singleTrip', labels) || 'Fahrt',
			value: currOrderItem ? (
				formatTripsAmountLabel(currOrderItem.trips, labels)
			) : (
				<Skeleton inline width="100%" height="100%" />
			),
		},
		{
			id: 'licenseplate',
			type: changelicenseplate ? 'edit' : undefined,
			label: getLabelValue('mp.licensePlate', labels) || 'Kennzeichen',
			value: currOrderItem ? (
				formatPlates(currOrderItem.licenseplate)
			) : (
				<Skeleton inline height="100%" width="100%" />
			),
			component: (
				<EnterPlates
					withAutoFocus
					border
					hasSpecialPlates
					insideOfModal
					plates={updatedLicencePlate}
					countryCode={countryCode}
					setPlates={(value: IPlatesValidatedState) => {
						setUpdatedLicencePlate(value);
					}}
					customerCountries={customerCountries}
					setHideModal={setHideModal}
				/>
			),
			hasErrors: !updatedLicencePlate.confirmed || updatedLicencePlate.error,
			hidden: hideModal,
			submitTracking: {
				button_click: {
					element: 'Kennzeichen ändern',
					label: 'Änderung erfolgreich',
					category: 'Account',
					trackOnce: true,
				},
			},
			handleChange: () => {
				if (updatedLicencePlate.confirmed) {
					updateOrder({
						orderDetailUuid,
						updatedLicencePlate,
						updatedVignetteDates: vignetteDates,
						labels,
						setLoading,
						setApiError,
						handleOrderException: orderExceptionHandler,
						updateNewOrderData,
					});
				}
			},
			handleClose: () => {
				if (licenseplate !== updatedLicencePlate.value) {
					setUpdatedLicencePlate({
						...updatedLicencePlate,
						value: convertPlatesToArray(licenseplate, countryCode, Boolean(licenseplateType)),
						specialPlates: Boolean(licenseplateType),
						error: false,
					});
				}
			},
		},
	];

	if (cancelable) {
		productSummary.push(
			deleteOrderedProduct({
				lang,
				currOrderItem,
				navigate,
				cartLabels: currentLabels,
				labels,
				setLoading,
				setApiError,
				handleOrderException: orderExceptionHandler,
				updateCancelledOrderInfo,
			}),
		);
	}

	return (
		<>
			<ContentLoader isLoading={loading} />
			{renderProductSummaryElement(productSummary, currOrderItem)}
			{apiError && (
				<Toast content={apiError} icon={<ErrorCrossIcon />} onToastFinished={() => setApiError(undefined)} />
			)}
		</>
	);
};

export const FlexServiceOrderSummary = ({ currOrderItem }: IProductOrderSummary) => {
	const navigate = useNavigate();
	const { contentfulLabels, updateNewOrderData } = useContext(VignetteContext);
	const currentLabels = contentfulLabels as ITypeVignettesOrderManagementFields;
	const { lang, labels, updateCancelledOrderInfo } = useContext(OfferContext);
	const [apiError, setApiError] = useState<string | undefined>(undefined);
	const [hideModal, setHideModal] = useState<boolean>(false);
	const [loading, setLoading] = useState(false);
	const {
		checkInDate,
		checkOutDate,
		changelicenseplate,
		cancelable,
		countryCode,
		licenseplate,
		licenseplateType,
		customerCountries,
		orderDetailUuid,
		serviceCatalogName,
		moduleDetail,
	} = currOrderItem;
	const [updatedLicencePlate, setUpdatedLicencePlate] = useState<IPlatesValidatedState>({
		value: convertPlatesToArray(currOrderItem.licenseplate, countryCode, Boolean(licenseplateType)),
		error: false,
		confirmed: false,
		countryCode,
		specialPlates: Boolean(licenseplateType),
	});
	const vignetteDates = convertVignetteIDateToDates(checkInDate, checkOutDate);
	const productSummary: IProductSummary[] = [
		{
			id: 'dates',
			label: getLabelValue('mp.valid', labels) || 'Gültig',
			value: currentLabels?.untilRevoked || 'Bis auf Widerruf',
		},
		{
			id: 'trips',
			type: 'trips',
			label: getLabelValue('mp.multipleTrips', labels) || 'Fahrten',
			value: currentLabels?.showTrips || 'Fahrtenübersicht anzeigen',
			handleOnClick: () => {
				sessionStorage.setItem('orderDetailUuid', orderDetailUuid);
				navigate(getUrlBasePath(`/trips-overview${setRouteLangParam(lang)}`));
			},
		},
		{
			id: 'licenseplate',
			type: changelicenseplate ? 'edit' : undefined,
			label: getLabelValue('mp.licensePlate', labels) || 'Kennzeichen',
			value: currOrderItem ? (
				formatPlates(currOrderItem.licenseplate)
			) : (
				<Skeleton inline height="100%" width="100%" />
			),
			component: (
				<EnterPlates
					withAutoFocus
					border
					hasSpecialPlates
					insideOfModal
					plates={updatedLicencePlate}
					countryCode={countryCode}
					setPlates={(value: IPlatesValidatedState) => {
						setUpdatedLicencePlate(value);
					}}
					customerCountries={customerCountries}
					setHideModal={setHideModal}
				/>
			),
			submitTracking: {
				button_click: {
					element: 'Kennzeichen ändern',
					label: 'Änderung erfolgreich',
					category: 'Account',
					trackOnce: true,
				},
			},
			hasErrors: !updatedLicencePlate.confirmed || updatedLicencePlate.error,
			hidden: hideModal,
			handleChange: () => {
				if (updatedLicencePlate.confirmed) {
					updateOrder({
						orderDetailUuid,
						updatedLicencePlate,
						updatedVignetteDates: vignetteDates,
						labels,
						setLoading,
						setApiError,
						handleOrderException: orderExceptionHandler,
						updateNewOrderData,
					});
				}
			},
			handleClose: () => {
				if (licenseplate !== updatedLicencePlate.value) {
					setUpdatedLicencePlate({
						...updatedLicencePlate,
						value: convertPlatesToArray(licenseplate, countryCode, Boolean(licenseplateType)),
						specialPlates: Boolean(licenseplateType),
						error: false,
					});
				}
			},
		},
	];

	if (cancelable) {
		productSummary.push({
			id: 'cancel product',
			type: 'cart',
			label: currentLabels?.cancelFlexService || `Flex-Service löschen`,
			value: (
				<span className="w-full flex gap-1.5 justify-end">
					<DeleteIcon bold />
					<Typography variant="title">
						{currentLabels?.cancelFlexService || 'Flex-Service löschen'}
					</Typography>
				</span>
			),
			component: renderCancellationOverlay(currOrderItem, currentLabels),
			submitButtonLabel: currentLabels?.cancelFlexService || 'Flex-Service löschen',
			submitTracking: {
				button_click: {
					element: 'Produkt stornieren',
					label: 'Stornierung erfolgreich',
					category: 'Account',
					product: serviceCatalogName,
					productVariant: moduleDetail,
				},
			},
			handleChange: () => {
				removeOrder({
					lang,
					orderDetailUuid,
					navigate,
					labels,
					updateCancelledOrderInfo,
					handleOrderException: orderExceptionHandler,
					setLoading,
					setApiError,
				});
			},
		});
	}

	return (
		<>
			<ContentLoader isLoading={loading} />
			{renderProductSummaryElement(productSummary, currOrderItem)}
			{apiError && (
				<Toast content={apiError} icon={<ErrorCrossIcon />} onToastFinished={() => setApiError(undefined)} />
			)}
		</>
	);
};
